/**
 * Targeting, swings, damage, death and morale.
 *
 * The rock-paper-scissors the player is meant to *see* lives in two places: the
 * damage-vs-armour table in `types.ts`, and the situational multipliers below
 * (braced spears against a charge, a flank, a shield that eats an arrow). Both
 * are deliberately chunky — a counter should read as "that melted", not as a
 * seven-percent efficiency gain.
 */

import { CORPSE_SOULS, MAP_H, MAP_W } from '../constants.ts';
import {
  alliedWith,
  areHostile,
  ArmourType,
  DamageType,
  damageMultiplier,
  OBSTACLE_COVER,
  Phase,
  Team,
  UF,
  UnitState,
  WallKind,
} from '../types.ts';
import { EV, LogTone, pushEvent, pushLog } from '../events.ts';
import { hostileMask, TEAM_ANY } from '../spatial.ts';
import { inCover, lineBlocked } from '../obstacles.ts';
import { highGroundMelee } from '../field.ts';
import { findCompany, spawnUnit, statsOf, type World } from '../world.ts';
import { unitType, unitTypeIndex, type UnitType } from '../../data/units.ts';
import { race } from '../../data/races.ts';
import { thrallThreshold, THRALL_HP_FRACTION_PENS } from './bindings.ts';
import { onVillagerDeath } from './demography.ts';
import { rankDamageMul } from '../../data/ranks.ts';
import { SIEGE_BREAKING_POINT, waveModifier } from './director.ts';
import { buildingType } from '../../data/buildings.ts';
import { chance, nextFloat } from '../../core/rng.ts';
import { Shot, spawnShot } from './projectiles.ts';
import { effRange, engageDistance, meleeReach } from './movement.ts';
import { awardHeroXp, heroDamageMul, HERO_XP_PER_KILL, HERO_XP_PER_THREAT } from './hero.ts';
import { awardCompanionXp } from './companions.ts';
import { heroWardMul } from './items.ts';
import {
  elevated,
  isClimber,
  parapetLeashRadius,
  reachableFromGround,
  unmountTile,
  standingOn,
  FALL_DAMAGE,
  FALL_STUN,
  PARAPET_COVER,
} from './garrison.ts';

/**
 * How far a unit will look for something to fight. Generous on purpose: a line
 * that stands still while it is shot from seven tiles away is not a line, it is
 * a target. Being *hit* also aggroes you onto whoever hit you (see damageUnit),
 * which is what stops missile troops kiting melee forever.
 */
const MELEE_AGGRO = 7;
/** How often a soldier with nothing to fight goes looking, in ticks. */
const TARGET_STRIDE = 3;
const ROUT_THRESHOLD = 0.22;
const ROUT_SECONDS = 5.5;
/** How long the Court's song holds a struck man against his own side. */
const MADNESS_SECONDS = 3.5;

/** Stance leashes, in tiles from the unit's formation slot. */
const LEASH = [2.2, 7.5, 26, 0];

/**
 * The reach at which a ground model can swat something out of the air.
 *
 * The air's exact analogue of `CLIMB_FREE_RANGE` in `garrison.ts`, and set to
 * the same idea: an ordinary spear is about 1.4 tiles and cannot touch a
 * gryphon, an ogre's arm or a whip at four can. Without an escape at the top
 * end, a giant would be helpless against a bird, which is silly in the other
 * direction.
 */
const AIR_REACH = 4;

/**
 * Can this attacker touch something that is flying?
 *
 * Wings or a bow — and a ladder is no use whatever against a gryphon, which is
 * why this is *not* `isClimber`: that one is about getting onto a wall, which a
 * sapper and a siege engine can do and neither of which can reach the sky.
 *
 * The whole guard was missing. `UF.Flying` is read by six systems and was set
 * by nothing in the catalogue, so nobody had ever noticed that `stepTargeting`
 * makes no distinction between air and ground: a spearman on the ground swung
 * at a flier overhead for full damage and a flier could be pinned in melee by
 * infantry. Resolved once per attacker, like `isClimber` and for the same
 * reason — nothing about the attacker's half changes between candidates.
 */
function canReachAir(attackerFlags: number, attackerReach: number): boolean {
  if ((attackerFlags & (UF.Flying | UF.Ranged)) !== 0) return true;
  return attackerReach >= AIR_REACH;
}

export function stepTargeting(w: World): void {
  const u = w.units;
  const high = u.slots.high;

  for (let i = 0; i < high; i++) {
    if (u.slots.alive[i] === 0) continue;
    if ((u.flags[i] & UF.Villager) !== 0) continue;
    if (u.state[i] === UnitState.Routing) {
      u.targetH[i] = -1;
      u.targetBuilding[i] = -1;
      u.targetWall[i] = -1;
      continue;
    }

    const t = unitType(u.typeId[i]);
    if (t.damage <= 0) continue;

    // Keep a valid target.
    const reach = effRange(w, i, t);
    /**
     * Whether this attacker can get at somebody standing on a wall — wings, a
     * ladder, an engine, or simply a bow. Resolved once per soldier rather than
     * once per candidate, for the same reason the stance leash is: nothing about
     * the answer's attacker half changes between the men it is looking at.
     */
    const climber = isClimber(u.flags[i]);
    /** And whether anything he is holding will reach a flier. See `canReachAir`. */
    const hitsAir = canReachAir(u.flags[i], reach);
    const cur = u.resolve(u.targetH[i]);
    if (cur >= 0) {
      const d2 = sq(u.x[i] - u.x[cur]) + sq(u.y[i] - u.y[cur]);
      const keep = Math.max(reach + 2.5, MELEE_AGGRO);
      // Re-checked and not merely checked on acquisition: a defender who was on
      // open ground when he was picked out may have climbed onto the parapet
      // since, and a grunt swinging at a man ten feet above him forever is the
      // exact failure this rule exists to prevent.
      if (
        d2 < keep * keep &&
        withinLeash(w, i, u.x[cur], u.y[cur]) &&
        ((u.wallTile[cur] < 0 && u.wallAt[cur] === -1) || reachableFromGround(w, cur, climber, reach)) &&
        // Re-checked rather than merely checked on acquisition, exactly as the
        // parapet is: a man who was on the ground when he was picked out may
        // have taken off since.
        (hitsAir || (u.flags[cur] & UF.Flying) === 0)
      ) {
        u.goalX[i] = u.x[cur];
        u.goalY[i] = u.y[cur];
        continue;
      }
      u.targetH[i] = -1;
    }

    /**
     * Looking for somebody new to fight is stride-limited; keeping the man you
     * are already fighting is not.
     *
     * The branch above — hold the current target if it is still in reach and
     * still inside the stance leash — runs every tick for everybody, so a
     * soldier in contact never wavers. What is gated is the *search*, and the
     * search is the expensive half: a rear-rank model with nothing in front of
     * it sweeps eighty cells of the spatial hash, finds nothing, and does it
     * again a thirtieth of a second later. In a thirty-thousand-model battle
     * most of the field is rear ranks.
     *
     * One tick in three, phased by unit index so the cost is spread evenly
     * rather than landing on every third tick at once. The worst case is that a
     * soldier notices an enemy a tenth of a second late, which is well inside
     * the time it takes to turn and swing. The offset comes from the unit index
     * and the tick counter and from nothing else — a phase drawn from anything
     * a clock knows would be a determinism bug.
     */
    if (u.targetH[i] < 0 && (i + w.tick) % TARGET_STRIDE !== 0) continue;

    const search = Math.max(reach + 1.6, MELEE_AGGRO);
    const madness = u.madnessT[i] > 0;
    const myTeam = u.team[i];
    /**
     * A company that has been told what to kill looks for it first.
     *
     * The ordinary search is "nearest hostile", which is right for a line
     * holding ground and useless as an order — a player who points at the
     * ram wants the ram. So a focused company sweeps a wider circle for men
     * of the named company before falling through to the normal answer, and
     * a madman ignores the order entirely because he is past taking one.
     */
    const focus = madness ? -1 : focusOf(w, i);
    if (focus >= 0) {
      const want = w.spatial.nearest(
        u.x[i],
        u.y[i],
        FOCUS_REACH,
        u,
        (k) =>
          k !== i &&
          u.hp[k] > 0 &&
          u.companyId[k] === focus &&
          areHostile(myTeam, u.team[k]) &&
          (hitsAir || (u.flags[k] & UF.Flying) === 0) &&
          ((u.wallTile[k] < 0 && u.wallAt[k] === -1) || reachableFromGround(w, k, climber, reach)),
      );
      if (want >= 0) {
        u.targetH[i] = u.handle(want);
        u.targetBuilding[i] = -1;
        u.targetWall[i] = -1;
        continue;
      }
    }
    /**
     * The leash is resolved once per soldier, not once per candidate.
     *
     * `withinLeash` used to be called from inside this predicate, and it looked
     * up the soldier's company by a linear scan of `w.companies` — so the cost
     * of picking a target was companies × candidates. Nothing about the answer
     * depends on which candidate is being tested, only on where the company's
     * anchor is, so it is hoisted whole.
     */
    const leash = leashOf(w, i);
    /**
     * Anybody who is *at war with the town* will kill villagers, and soldiers
     * come first. Alliance-driven rather than `=== Team.Invader`, so a second
     * horde on a shared field behaves like the first one rather than treating
     * a farmhand as a priority target.
     */
    const skipVillagers = areHostile(myTeam, Team.Player) && w.tick % 3 !== 0;
    const j = w.spatial.nearest(
      u.x[i],
      u.y[i],
      search,
      u,
      (k) => {
        if (k === i) return false;
        if (u.hp[k] <= 0) return false;
        if (skipVillagers && (u.flags[k] & UF.Villager) !== 0) {
          // Raiders will kill villagers, but soldiers come first.
          return false;
        }
        // The one hostility test in the targeting sweep, and the reason
        // `areHostile` exists: `u.team[k] !== myTeam` reads as "not mine" and
        // means "the other side" only while there are two of them. A madman is
        // past taking a side at all, which is why he skips it.
        const hostile = madness ? true : areHostile(myTeam, u.team[k]);
        if (!hostile) return false;
        /**
         * The wall.
         *
         * A man on an intact curtain is simply not a target for anything on the
         * ground that cannot climb or shoot. Refusing him here rather than
         * letting the swing fail later is what makes the horde behave properly:
         * the search finds nobody, falls through to `pickStructureTarget`, and
         * the grunts start hitting the stone under the archers instead — which
         * is what a besieger with no ladders ought to do.
         *
         * The guard is inline rather than a call so that the overwhelmingly
         * common case — a candidate who is not on a wall at all — costs one
         * array read.
         */
        if ((u.wallTile[k] >= 0 || u.wallAt[k] !== -1) && !reachableFromGround(w, k, climber, reach)) return false;
        /**
         * The air, and it is the same rule as the wall from the other
         * direction: something overhead is simply not a target for a man with a
         * spear. Refusing it here rather than letting the swing fail later is
         * what makes it read properly — the search finds nobody, the company
         * holds its ground, and the answer to a flier is archers or your own
         * wings rather than everybody flailing upward.
         *
         * One flag read on the overwhelmingly common case of a candidate who is
         * not flying at all.
         */
        if (!hitsAir && (u.flags[k] & UF.Flying) !== 0) return false;
        if (leash === null) return true;
        if (leash.r2 < 0) return false;
        return sq(u.x[k] - leash.x) + sq(u.y[k] - leash.y) < leash.r2;
      },
      // Madness turns a man on his own side, so it can accept anybody.
      madness ? TEAM_ANY : hostileMask(myTeam),
    );

    if (j >= 0) {
      u.targetH[i] = u.handle(j);
      u.targetBuilding[i] = -1;
      u.targetWall[i] = -1;
      u.goalX[i] = u.x[j];
      u.goalY[i] = u.y[j];
      continue;
    }

    u.targetH[i] = -1;
    /**
     * There is no owner field on a wall tile, so "may I chew on the scenery" is
     * "am I at war with whoever owns the scenery" — `w.wallTeam`.
     *
     * This read `Team.Player`, which is right in every settlement and was the
     * whole of the wedge on an assault field. The player's own troops were
     * hostile to nothing on the map, so a besieging army would not swing at the
     * wall in front of it *even once*: it pressed against the stone with no
     * target, no route and no way through, which is precisely the report. The
     * roofs go with the walls because they are the same town.
     */
    if (areHostile(myTeam, w.wallTeam)) pickStructureTarget(w, i, t);
    else if (u.targetBuilding[i] >= 0 || u.targetWall[i] >= 0) {
      u.targetBuilding[i] = -1;
      u.targetWall[i] = -1;
    }
  }
}

/**
 * Where to stand to hit a building: the nearest point on its footprint, not its
 * centre.
 *
 * This distinction is the difference between a horde that sacks a town and one
 * that mills around outside it. `stepAttacks` measures the gap as
 * `goal - position`, and a melee grunt's reach works out at about 1.4 tiles —
 * but a building's footprint is *solid geometry*, so a unit standing flush
 * against a 3×3 Hearth is still 1.8 tiles from its centre and can never close
 * the difference. Aiming at the centre therefore made every building 3×3 or
 * larger — the Hearth, farms, barracks, the market — literally immune to
 * infantry. Only long-reach models like ogres could touch them, which is why it
 * looked like slow chip damage rather than a bug.
 *
 * Clamping the unit's own position into the rectangle gives the closest point
 * on it, which is what "walk up to the wall of the building and hit it" means.
 */
function aimAtBuilding(w: World, i: number, b: number): void {
  const u = w.units;
  const def = buildingType(w.buildings.typeId[b]);
  const x0 = w.buildings.tx[b];
  const y0 = w.buildings.ty[b];
  u.goalX[i] = Math.min(Math.max(u.x[i], x0), x0 + def.w);
  u.goalY[i] = Math.min(Math.max(u.y[i], y0), y0 + def.h);
}

/** Invaders with nothing to fight chew on whatever is between them and the goal. */
function pickStructureTarget(w: World, i: number, t: UnitType): void {
  const u = w.units;
  /**
   * A flier goes over the curtain, so a wall is never its target — but the roof
   * under it is.
   *
   * This used to clear *both* and return, which meant an invading flier could
   * not damage a single structure in the game: it would cross the walls, find
   * nothing on the ground it wanted, and circle. The wall half of that is right
   * and stays; the building half was the whole of "why can a gryphon not burn a
   * granary".
   */
  const flier = (u.flags[i] & UF.Flying) !== 0;
  if (flier) u.targetWall[i] = -1;

  // Still valid?
  const tw = flier ? -1 : u.targetWall[i];
  if (tw >= 0 && w.map.wall[tw] !== WallKind.None && w.map.wallHp[tw] > 0) {
    const wx = (tw % MAP_W) + 0.5;
    const wy = ((tw / MAP_W) | 0) + 0.5;
    if (sq(u.x[i] - wx) + sq(u.y[i] - wy) < 9) {
      u.goalX[i] = wx;
      u.goalY[i] = wy;
      return;
    }
  }
  const tb = u.targetBuilding[i];
  if (tb >= 0 && w.buildings.isAlive(tb)) {
    const def = buildingType(w.buildings.typeId[tb]);
    const bx = w.buildings.tx[tb] + def.w / 2;
    const by = w.buildings.ty[tb] + def.h / 2;
    if (sq(u.x[i] - bx) + sq(u.y[i] - by) < 16) {
      aimAtBuilding(w, i, tb);
      return;
    }
  }
  u.targetWall[i] = -1;
  u.targetBuilding[i] = -1;

  // Anything adjacent gets hit first.
  const cx = Math.floor(u.x[i]);
  const cy = Math.floor(u.y[i]);
  const reach = Math.max(1, Math.ceil(t.range));
  let bestWall = -1;
  let bestWallHp = Infinity;
  let bestBuild = -1;
  let bestBuildD = Infinity;

  for (let dy = -reach; dy <= reach; dy++) {
    for (let dx = -reach; dx <= reach; dx++) {
      const x = cx + dx;
      const y = cy + dy;
      if (x < 0 || y < 0 || x >= MAP_W || y >= MAP_H) continue;
      const k = w.map.idx(x, y);
      if (!flier && w.map.wall[k] !== WallKind.None && w.map.wallHp[k] > 0) {
        // Prefer the weakest adjacent segment — the horde finds the soft spot.
        if (w.map.wallHp[k] < bestWallHp) {
          bestWallHp = w.map.wallHp[k];
          bestWall = k;
        }
      }
      const b = w.map.building[k];
      if (b >= 0 && areHostile(u.team[i], w.buildings.team[b])) {
        const d = sq(x + 0.5 - u.x[i]) + sq(y + 0.5 - u.y[i]);
        if (d < bestBuildD) {
          bestBuildD = d;
          bestBuild = b;
        }
      }
    }
  }

  // A tunneller does not stop to gnaw what he can go under — walls are never
  // his target, and he keeps walking toward whatever is behind them.
  if (t.ability === 'burrow') bestWall = -1;

  const sapper = (u.flags[i] & (UF.Sapper | UF.Siege)) !== 0;
  if (bestWall >= 0 && (sapper || bestBuild < 0)) {
    u.targetWall[i] = bestWall;
    u.goalX[i] = (bestWall % MAP_W) + 0.5;
    u.goalY[i] = ((bestWall / MAP_W) | 0) + 0.5;
    return;
  }
  if (bestBuild >= 0) {
    u.targetBuilding[i] = bestBuild;
    aimAtBuilding(w, i, bestBuild);
    return;
  }
  if (bestWall >= 0) {
    u.targetWall[i] = bestWall;
    u.goalX[i] = (bestWall % MAP_W) + 0.5;
    u.goalY[i] = ((bestWall / MAP_W) | 0) + 0.5;
  }
}

/**
 * A soldier's stance leash, resolved once: the anchor it is tethered to and the
 * square of how far it may stray. `null` means "not tethered at all", and a
 * negative `r2` means Hold — a stance that refuses every target rather than
 * accepting any.
 *
 * Split out of `withinLeash` so the targeting sweep can hoist it out of its
 * inner predicate. `withinLeash` is kept for the handful of one-shot callers
 * and is now a thin wrapper, so the two can never disagree.
 */
interface Leash {
  x: number;
  y: number;
  r2: number;
}

/**
 * Scratch, because this is called once per soldier per tick and a fresh object
 * each time is thirty thousand allocations a tick at city scale — enough GC
 * churn to show up as a system of its own on the profile. The tick is
 * single-threaded and the value is consumed before the next call.
 */
const leashScratch: Leash = { x: 0, y: 0, r2: 0 };

/**
 * How far a focused company will look for what it was told to kill.
 *
 * Generously wider than an ordinary acquisition — the point of the order is
 * to reach past what is nearest — but finite, so a company cannot snipe
 * across the whole map and an order that has run out of sight simply lapses
 * back to fighting whatever is in front of it.
 */
const FOCUS_REACH = 26;

/** The company this soldier has been told to break, or −1. */
function focusOf(w: World, i: number): number {
  const cid = w.units.companyId[i];
  if (cid < 0) return -1;
  const c = findCompany(w, cid);
  if (!c || c.focusCompany < 0) return -1;
  const foe = findCompany(w, c.focusCompany);
  // The order clears itself rather than outliving its target: a company still
  // marching at a memory is worse than one that never obeyed.
  if (!foe || foe.dead || foe.members.length === 0) {
    c.focusCompany = -1;
    return -1;
  }
  return foe.id;
}

function leashOf(w: World, i: number): Leash | null {
  const u = w.units;
  /**
   * Deliberately still the player's own team and not the whole alliance.
   *
   * A stance leash is an *order* — it is how Hold, Guard and Pursue differ —
   * and only companies the player commands take one. An AI banner fighting
   * alongside you steers itself, exactly as the enemy always has, so it hunts
   * freely. If a second human ever holds the second banner, this is the line
   * that has to learn about it.
   */
  if (u.team[i] !== Team.Player) return null;
  /**
   * A man on a wall is tethered to his own stone, not to his company's banner.
   *
   * The anchor of a garrisoned company sits at the inside foot of the section it
   * was pointed at, which may be a hundred tiles from the flank of its own line
   * once it has spread out along the curtain — and Guard's leash is seven and a
   * half. Left on the banner, half a garrison would refuse every target in front
   * of it. What a man on a parapet may shoot at is what he can see from where he
   * is standing, which is exactly his own reach.
   */
  const post = standingOn(w, i);
  if (post >= 0) {
    leashScratch.x = (post % MAP_W) + 0.5;
    leashScratch.y = ((post / MAP_W) | 0) + 0.5;
    const r = parapetLeashRadius(w, i);
    leashScratch.r2 = r * r;
    return leashScratch;
  }
  const cid = u.companyId[i];
  if (cid < 0) return null;
  const c = findCompany(w, cid);
  if (!c) return null;
  const leash = LEASH[c.stance] ?? 8;
  leashScratch.x = c.ax;
  leashScratch.y = c.ay;
  leashScratch.r2 = leash <= 0 ? -1 : leash * leash;
  return leashScratch;
}

function withinLeash(w: World, i: number, tx: number, ty: number): boolean {
  const l = leashOf(w, i);
  if (l === null) return true;
  if (l.r2 < 0) return false;
  return sq(tx - l.x) + sq(ty - l.y) < l.r2;
}

export function stepAttacks(w: World, dt: number): void {
  const u = w.units;
  const high = u.slots.high;

  for (let i = 0; i < high; i++) {
    if (u.slots.alive[i] === 0) continue;
    if (u.stunT[i] > 0) continue;
    const t = unitType(u.typeId[i]);
    if (t.damage <= 0) continue;

    if (u.attackCd[i] > 0) u.attackCd[i] -= dt;

    let rate = t.attacksPerSec;
    if (u.team[i] === Team.Player) rate *= w.mods.attackSpeed;
    if (u.rallyT[i] > 0) rate *= 1.25;
    if (u.slowT[i] > 0) rate *= 0.62;
    const cycle = 1 / Math.max(0.05, rate);

    if (u.swing[i] > 0) {
      const prev = u.swing[i];
      u.swing[i] += dt / cycle;
      if (prev < t.windup && u.swing[i] >= t.windup) contact(w, i, t);
      if (u.swing[i] >= 1) {
        u.swing[i] = 0;
        if (u.state[i] === UnitState.Attacking) u.state[i] = UnitState.Idle;
      }
      continue;
    }

    if (!hasTarget(w, i)) continue;
    const dx = u.goalX[i] - u.x[i];
    const dy = u.goalY[i] - u.y[i];
    // A bowman with a man in his face is not a bowman.
    const locked = meleeLocked(w, i, dx, dy);
    const reach = locked ? meleeReach(w, i) : engageDistance(w, i, t);
    if (dx * dx + dy * dy > reach * reach) continue;

    /**
     * You cannot shoot through a boulder.
     *
     * Asked here — once, of the man about to release — rather than in the
     * targeting sweep, which would be a raycast per candidate and by a wide
     * margin the most expensive thing in the tick. The cost of asking late is
     * that a bowman notices a rock one swing after he first sees the man behind
     * it, which is a tenth of a second and invisible. The cost of not asking at
     * all is scatter that the fight ignores, which is the whole point of it.
     *
     * `lineBlocked` returns false immediately when the world has no scatter, so
     * every siege in the game pays one null check.
     */
    if (!locked && w.obstacleGrid !== null && lineBlocked(w, u.x[i], u.y[i], u.goalX[i], u.goalY[i])) {
      // Drop him and look again next stride: standing there aiming at stone is
      // exactly the stall the wall rule was written to prevent.
      u.targetH[i] = -1;
      u.targetBuilding[i] = -1;
      u.targetWall[i] = -1;
      continue;
    }

    u.swing[i] = 0.0001;
    u.state[i] = UnitState.Attacking;
    u.facing[i] = Math.atan2(dy, dx);
  }
}

/**
 * Missile troops are locked into melee when something reaches them: they stop
 * shooting and fight badly with whatever is in their off hand. Without this,
 * twelve archers beat eight cavalry in a stand-up brawl, which is nonsense and
 * quietly destroys the whole counter triangle.
 */
export const MELEE_LOCK_RANGE = 1.7;
export const RANGED_MELEE_PENALTY = 0.4;

export function meleeLocked(w: World, i: number, dx: number, dy: number): boolean {
  if ((w.units.flags[i] & UF.Ranged) === 0) return false;
  // Out of arrows is the same problem as having a man in your face.
  if (w.units.ammo[i] <= 0) return true;
  return dx * dx + dy * dy < MELEE_LOCK_RANGE * MELEE_LOCK_RANGE;
}

function hasTarget(w: World, i: number): boolean {
  const u = w.units;
  if (u.resolve(u.targetH[i]) >= 0) return true;
  if (u.targetBuilding[i] >= 0 && w.buildings.isAlive(u.targetBuilding[i])) return true;
  const tw = u.targetWall[i];
  if (tw >= 0 && w.map.wall[tw] !== WallKind.None && w.map.wallHp[tw] > 0) return true;
  return false;
}

/** The contact frame: this is where a swing actually becomes a wound. */
function contact(w: World, i: number, t: UnitType): void {
  const u = w.units;
  const locked = meleeLocked(w, i, u.goalX[i] - u.x[i], u.goalY[i] - u.y[i]);
  const melee = t.range <= 0.8 || locked;
  const target = u.resolve(u.targetH[i]);

  let damage = t.damage * (locked ? RANGED_MELEE_PENALTY : 1);

  // Swinging is work. Tired troops hit softer — up to a third softer.
  u.fatigue[i] = Math.min(1, u.fatigue[i] + 0.035 / Math.max(0.2, t.stamina));
  damage *= 1 - u.fatigue[i] * 0.34;

  if (!locked && (u.flags[i] & UF.Ranged) !== 0) {
    u.ammo[i] = Math.max(0, u.ammo[i] - 1);
  }
  if (u.team[i] === Team.Player) {
    damage *= (w.faction.bonuses.unitDamage ?? 1) * w.mods.unitDamage * rankDamageMul(w.unitRanks[t.id] ?? 0);
  }
  if (u.rallyT[i] > 0) damage *= 1.15;
  const cid = u.companyId[i];
  if (cid >= 0) {
    const c = findCompany(w, cid);
    if (c) damage *= 1 + c.rank * 0.08;
  }
  // A hero's class and level are his own stats, not an army-wide modifier, so
  // they cannot live in `mods` with everything else. `heroId` is -1 on every
  // other model in the game, which makes this a single branch and no more.
  if (u.heroId[i] >= 0) damage *= heroDamageMul(w, i);
  // Moonbound hunt by night.
  if (u.team[i] === Team.Player && w.faction.id === 'moonbound' && w.phase === Phase.Siege) {
    damage *= 1.25;
  }

  const dmgType = locked ? DamageType.Slash : t.dmgType;

  if (target >= 0) {
    damage *= situational(w, i, target, t);
    if (melee) {
      const kx = u.x[target] - u.x[i];
      const ky = u.y[target] - u.y[i];
      const kl = Math.hypot(kx, ky) || 1;
      applyMelee(w, i, target, damage, t, dmgType, kx / kl, ky / kl);
    } else {
      spawnShot(
        w,
        shotKind(t),
        u.x[i],
        u.y[i],
        u.handle(target),
        u.x[target],
        u.y[target],
        damage,
        t.dmgType,
        u.team[i],
        t.splash,
        u.handle(i),
      );
    }
    return;
  }

  // Structures.
  const tb = u.targetBuilding[i];
  if (tb >= 0 && w.buildings.isAlive(tb)) {
    if (melee) {
      damageBuilding(w, tb, damage, t.dmgType);
      pushEvent(w.events, EV.StructureHit, u.goalX[i], u.goalY[i], damage, 0, u.team[i]);
    } else {
      spawnShot(
        w,
        shotKind(t),
        u.x[i],
        u.y[i],
        -1,
        u.goalX[i],
        u.goalY[i],
        damage,
        t.dmgType,
        u.team[i],
        t.splash,
        u.handle(i),
      );
    }
    return;
  }

  const tw = u.targetWall[i];
  if (tw >= 0) {
    if (melee) {
      damageWall(w, tw, damage, t.dmgType);
    } else {
      spawnShot(
        w,
        shotKind(t),
        u.x[i],
        u.y[i],
        -1,
        (tw % MAP_W) + 0.5,
        ((tw / MAP_W) | 0) + 0.5,
        damage,
        t.dmgType,
        u.team[i],
        t.splash,
        u.handle(i),
      );
    }
  }
}

/** The multipliers a player should be able to feel without reading a tooltip. */
function situational(w: World, i: number, j: number, t: UnitType): number {
  const u = w.units;
  let m = 1;

  // Braced spears gut a charge.
  if ((u.flags[i] & UF.Spear) !== 0 && (u.flags[j] & UF.Cavalry) !== 0) m *= 1.9;

  // The charge is a *burst*, not a passive bonus. A rider has to have been
  // moving, and having spent it must disengage and come round again before it
  // is worth anything. This is the whole reason cavalry loses a grind and wins
  // a flank — and it is why a spear line that stands still is the answer.
  if ((u.flags[i] & UF.Cavalry) !== 0 && u.attackCd[i] <= 0) {
    const spd = Math.hypot(u.vx[i], u.vy[i]);
    if (spd > u.speed[i] * 0.5) {
      const braced = (u.flags[j] & UF.Spear) !== 0 && Math.hypot(u.vx[j], u.vy[j]) < 0.4;
      if (!braced) {
        m *= 2.4 * (u.team[i] === Team.Player ? w.mods.cavalryCharge : 1);
        pushEvent(w.events, EV.Impact, u.x[j], u.y[j], 0.9, 93, u.team[i], u.vx[i], u.vy[i]);
      }
      u.attackCd[i] = 3.4;
    }
  }
  /**
   * The high ground.
   *
   * Eleven per cent a tile of height, capped at a tile and a half either way,
   * and it is a *swing* rather than a bonus: fighting up a slope costs you the
   * same as fighting down it gains. Capped hard on purpose — the ridge is meant
   * to be worth taking, not worth winning on its own, and a rule that could
   * double a melee would make every battle a race for one hill and nothing
   * else. Exactly 1 on any world with no relief, which is the cheap exit every
   * settlement takes; see `highGroundMelee`.
   */
  m *= highGroundMelee(w, i, j);

  // Flanking: struck from behind.
  const dx = u.x[i] - u.x[j];
  const dy = u.y[i] - u.y[j];
  const fx = Math.cos(u.facing[j]);
  const fy = Math.sin(u.facing[j]);
  const dot = (dx * fx + dy * fy) / (Math.hypot(dx, dy) || 1);
  if (dot < -0.35) m *= 1.22;
  // Siege eats structures, not people.
  if (t.dmgType === DamageType.Siege && (u.flags[j] & UF.Large) === 0) m *= 0.85;

  return m;
}

function applyMelee(
  w: World,
  i: number,
  j: number,
  damage: number,
  t: UnitType,
  dmgType: number,
  kx: number,
  ky: number,
): void {
  const u = w.units;
  if (t.splash > 0) {
    w.spatial.query(u.x[j], u.y[j], t.splash, u, (k, d2) => {
      if (!areHostile(u.team[i], u.team[k])) return;
      const fall = 1 - Math.sqrt(d2) / t.splash;
      damageUnit(w, k, damage * (0.5 + 0.5 * fall), dmgType, u.x[i], u.y[i], i, kx, ky);
    });
    return;
  }
  damageUnit(w, j, damage, dmgType, u.x[i], u.y[i], i, kx, ky);
}

function shotKind(t: UnitType): number {
  switch (t.vis.weapon) {
    case 'bow':
      return Shot.Arrow;
    case 'crossbow':
      return Shot.Bolt;
    case 'gun':
      return Shot.Bullet;
    case 'staff':
      return Shot.Magic;
    default:
      break;
  }
  if (t.dmgType === DamageType.Siege) return Shot.Siege;
  if (t.dmgType === DamageType.Poison) return Shot.Spit;
  if (t.dmgType === DamageType.Arcane || t.dmgType === DamageType.Fire) return Shot.Magic;
  return Shot.Stone;
}

// ── Damage application ─────────────────────────────────────────────────────

export function damageUnit(
  w: World,
  j: number,
  raw: number,
  dmgType: number,
  srcX: number,
  srcY: number,
  attacker = -1,
  knockX = 0,
  knockY = 0,
): void {
  const u = w.units;
  if (u.slots.alive[j] === 0 || u.hp[j] <= 0) return;

  const t = unitType(u.typeId[j]);
  let dmg = raw * damageMultiplier(dmgType, t.armour);

  // Black Rain: fires gutter. Halves fire damage from every source, which
  // includes the player's own — the panel says "fires gutter", not "their
  // fires gutter", and a modifier that only ever helps you is not weather.
  if (dmgType === DamageType.Fire && waveModifier(w, 'rain')) dmg *= 0.5;

  // A shield facing the shot eats it.
  let blocked = false;
  if ((u.flags[j] & UF.Shielded) !== 0) {
    const dx = srcX - u.x[j];
    const dy = srcY - u.y[j];
    const fx = Math.cos(u.facing[j]);
    const fy = Math.sin(u.facing[j]);
    const dot = (dx * fx + dy * fy) / (Math.hypot(dx, dy) || 1);
    if (dot > 0.25 && chance(w.rng, 0.3)) {
      dmg *= 0.12;
      blocked = true;
    }
  }
  if ((u.flags[j] & UF.SpellEater) !== 0 && (dmgType === DamageType.Arcane || dmgType === DamageType.Fire)) {
    dmg *= 0.45;
  }
  if (u.shieldT[j] > 0) dmg *= 0.4;
  /**
   * Crenellations.
   *
   * A missile that has to travel *upward* into a wall mostly hits the wall.
   * Arrows and bolts only — a boulder does not care what a merlon is, and
   * neither does fire — which is what makes manning the parapet the answer to a
   * horde of archers rather than a way to lose your bowmen faster. It is also
   * the reason a breach is dangerous twice over: the men beside it lose this as
   * well as their unreachability.
   */
  if ((dmgType === DamageType.Arrow || dmgType === DamageType.Pierce) && elevated(w, j)) {
    dmg *= PARAPET_COVER;
  }
  /**
   * Trees.
   *
   * The same rule as the parapet, from the other direction: a canopy eats
   * missiles and does nothing about a sword, which is what makes a wood a place
   * a beaten company can pull back into rather than a slightly slower patch of
   * ground. Arrows and pierce only, for the reason the crenellations are — a
   * boulder does not care about branches.
   *
   * Costs one null check on every world with no scatter in it, which is every
   * settlement, and every point of damage in the game comes through here.
   */
  if (
    w.obstacleGrid !== null &&
    (dmgType === DamageType.Arrow || dmgType === DamageType.Pierce) &&
    inCover(w, u.x[j], u.y[j])
  ) {
    dmg *= 1 - OBSTACLE_COVER;
  }
  /**
   * The hero's own harness.
   *
   * The mirror of `heroDamageMul` above, and here for the same reason: a
   * breastplate worn by one man is a personal stat and cannot live in an
   * army-wide bag. The armour's *other* half — the part that makes the whole
   * line tougher — does go through `mods`, which is why this is only ever a
   * single branch on a field that is −1 for every other model in the game.
   */
  if (u.heroId[j] >= 0 && u.handle(j) === w.hero.unitH) dmg *= heroWardMul(w);

  u.hp[j] -= dmg;
  u.lastHitTick[j] = w.tick;

  // Being shot is information. A unit with nothing else to do turns on whoever
  // hit it, however far away that is — subject to its stance leash.
  if (
    attacker >= 0 &&
    u.slots.alive[attacker] === 1 &&
    u.targetH[j] < 0 &&
    (u.flags[j] & UF.Villager) === 0 &&
    u.state[j] !== UnitState.Routing &&
    withinLeash(w, j, u.x[attacker], u.y[attacker]) &&
    // Being shot from a parapet you cannot climb is information you can do
    // nothing with. Turning on the archer anyway would walk the whole horde to
    // the foot of the wall to stand there swinging at the air, instead of
    // hitting the stone that is actually in the way.
    ((u.wallTile[attacker] < 0 && u.wallAt[attacker] === -1) ||
      reachableFromGround(w, attacker, isClimber(u.flags[j]), effRange(w, j))) &&
    // Being shot from above you cannot reach is information you can do nothing
    // with, exactly as being shot from a parapet is. Turning on the flier would
    // walk the company under it to stand there swinging at the air.
    (canReachAir(u.flags[j], effRange(w, j)) || (u.flags[attacker] & UF.Flying) === 0)
  ) {
    u.targetH[j] = u.handle(attacker);
    u.targetBuilding[j] = -1;
    u.targetWall[j] = -1;
    u.goalX[j] = u.x[attacker];
    u.goalY[j] = u.y[attacker];
  }

  const stats = statsOf(w);
  if (attacker >= 0 && u.slots.alive[attacker] === 1 && u.team[attacker] === Team.Player) {
    stats.damageDealt += dmg;
    if (w.mods.lifesteal > 0) {
      u.hp[attacker] = Math.min(u.maxHp[attacker], u.hp[attacker] + dmg * w.mods.lifesteal);
    }
  }
  if (u.team[j] === Team.Player) {
    stats.damageTaken += dmg;
    // Thorns answer melee only; `knock` is non-zero exactly for melee contact.
    if (w.mods.thorns > 0 && attacker >= 0 && (knockX !== 0 || knockY !== 0)) {
      u.hp[attacker] -= w.mods.thorns;
      pushEvent(w.events, EV.Hit, u.x[attacker], u.y[attacker], w.mods.thorns, 0, u.team[attacker]);
      if (u.hp[attacker] <= 0) killUnit(w, attacker, j);
    }
  }

  // Knockback, scaled by damage and mass. A man behind a parapet does not get
  // shoved anywhere — and if he did, it would be off his own wall and into the
  // stone beside it, where the collision pass would wedge him.
  if (
    (u.flags[j] & (UF.Large | UF.Champion)) === 0 &&
    (u.wallTile[j] < 0 && u.wallAt[j] === -1) &&
    (knockX !== 0 || knockY !== 0)
  ) {
    const push = Math.min(3.4, dmg * 0.05);
    u.knockX[j] += knockX * push;
    u.knockY[j] += knockY * push;
  }

  // Morale: taking a hit is bad for the nerves; a big one is worse.
  u.morale[j] -= Math.min(0.16, dmg / Math.max(1, u.maxHp[j]) * 0.5);

  if (blocked) {
    pushEvent(w.events, EV.Block, u.x[j], u.y[j], dmg, 0, u.team[j], knockX, knockY);
  } else {
    pushEvent(w.events, EV.Hit, u.x[j], u.y[j], dmg, u.typeId[j], u.team[j], knockX, knockY);
  }

  if (u.hp[j] <= 0) {
    killUnit(w, j, attacker);
    return;
  }

  /**
   * The song takes you: the Drowned Court's `madness`, wired at last.
   *
   * Everything downstream already existed — the targeting sweep turns a
   * maddened model on his own side (`madness ? TEAM_ANY : ...`), the timer
   * decays in `stepMorale`, and Sanctified Ground cleanses it — but nothing
   * in the game ever *set* `madnessT`, so the Court's signature was a comment.
   * The source is the blow, through the one door every blow arrives at, which
   * is what lets the Abyssal Choir's arcane volley and the Abyssal's splash
   * both carry it with one rule. The fear-proof are madness-proof for the
   * same reason they are fear-proof — the risen and the engines have nothing
   * for the song to take — and a villager is spared not out of mercy but
   * because a mad villager is a comedy, not a threat.
   */
  if (
    attacker >= 0 &&
    u.slots.alive[attacker] === 1 &&
    areHostile(u.team[attacker], u.team[j]) &&
    (u.flags[j] & (UF.Undead | UF.Summoned | UF.Champion | UF.Siege | UF.Villager)) === 0 &&
    unitType(u.typeId[attacker]).ability === 'madness'
  ) {
    u.madnessT[j] = Math.max(u.madnessT[j], MADNESS_SECONDS);
    // The turn is a fact worth a flash on the field.
    pushEvent(w.events, EV.Rout, u.x[j], u.y[j], 2, u.typeId[j], u.team[j]);
  }

  /**
   * The Drowned Court takes what it beats.
   *
   * **The one door.** Every point of damage in the game — a swing, an arrow, a
   * boulder, a power, a tower, poison on the ground — arrives here, because this
   * is the only function that writes `u.hp`. A capture hung off the melee
   * contact path would have been a capture the Court's archers and its Thornwright
   * poison could never make, and a capture hung off `Cmd`-anything would have
   * been one the invasion never triggered at all.
   *
   * The cheap outer test first, and it matters: `thrallThreshold` walks the
   * building store, and this branch is evaluated on every blow that lands. The
   * widest possible band is a compile-time constant, a model only ever enters it
   * once because entering it removes him, and only then is the real threshold
   * asked for.
   */
  if (
    areHostile(Team.Player, u.team[j]) &&
    u.hp[j] < u.maxHp[j] * THRALL_HP_FRACTION_PENS &&
    race(w.raceId).binding === 'thralls' &&
    u.hp[j] < u.maxHp[j] * thrallThreshold(w)
  ) {
    takeThrall(w, j, attacker);
  }
}

/**
 * Carried off rather than killed.
 *
 * Deliberately *not* `killUnit` with a flag. A thrall is not a casualty: he
 * leaves no corpse, so he yields no Souls and stains no ground, and his own side
 * does not grieve for him because as far as they can tell he simply stopped
 * being there. What he does do is count — for the company that took him, which
 * is where the veterancy belongs, and for the pens, which is the muster.
 */
function takeThrall(w: World, j: number, attacker: number): void {
  const u = w.units;
  const t = unitType(u.typeId[j]);
  w.thralls += 1;
  statsOf(w).kills++;
  pushEvent(w.events, EV.Levelup, u.x[j], u.y[j], 1, u.typeId[j], u.team[j]);
  if (attacker >= 0 && u.slots.alive[attacker] === 1) {
    const acid = u.companyId[attacker];
    if (acid >= 0) {
      const ac = findCompany(w, acid);
      if (ac) {
        ac.kills++;
        ac.xp += t.threat * (ac.team === Team.Player ? w.mods.veterancyRate : 1);
      }
    }
  }
  awardHeroXp(w, HERO_XP_PER_KILL + t.threat * HERO_XP_PER_THREAT);
  if (w.tick % 120 === 0) {
    pushLog(w.events, 'Another one goes down the water stairs. He will be along.', LogTone.Good);
  }
  u.kill(j);
}

/**
 * `mourned = false` for a death nobody should feel: a summon reaching the end
 * of its own duration. It still leaves the field, still stains the ground and
 * still frees its slot — it is simply not a casualty. Booking it as one
 * inflated the run's losses and, far worse, rippled morale through every friend
 * within four tiles. `pale_summons` raises a dozen skeletons on a shared timer,
 * so they all wink out on the same tick and the stacked ripple was enough to
 * rout the line that summoned them, at the exact moment the player was already
 * under pressure.
 */
export function killUnit(w: World, j: number, attacker = -1, mourned = true): void {
  const u = w.units;
  if (u.slots.alive[j] === 0) return;
  const t = unitType(u.typeId[j]);
  const stats = statsOf(w);
  const big = (u.flags[j] & (UF.Large | UF.Champion | UF.Siege)) !== 0;

  pushEvent(w.events, EV.Death, u.x[j], u.y[j], big ? 2 : 1, u.typeId[j], u.team[j], u.vx[j], u.vy[j]);

  // Blood and ash stain the ground where they fall.
  const tx = Math.floor(u.x[j]);
  const ty = Math.floor(u.y[j]);
  if (w.map.inBounds(tx, ty)) {
    const k = w.map.idx(tx, ty);
    w.map.stain[k] = Math.min(255, w.map.stain[k] + (big ? 90 : 34));
  }

  /**
   * The Rotmoot's dead do not lie down quietly.
   *
   * `burst` — the Shambler's and the Bloat Bruiser's signature, named in the
   * data since the roster was written and wired to nothing until now. An
   * instant puff of rot rather than a lingering zone, deliberately: a wiped
   * company of eighteen Shamblers pushing eighteen poison zones through the
   * spatial hash for two seconds each was the alternative, and a one-shot
   * splash is bounded whatever dies. It hurts *everyone* in the puff, the
   * moot included, so packed Shamblers chain-pop — that is the fight the
   * ability was named for, and the reason to kill them at range. The dot
   * arrives as `poisonT`, the same door the poison zones use. Recursion
   * through `damageUnit → killUnit` terminates because each model dies once.
   */
  if (t.ability === 'burst') {
    const r = 1.4 + u.radius[j] * 1.6;
    const base = 8 + t.threat * 0.5;
    const bx = u.x[j];
    const by = u.y[j];
    w.spatial.query(bx, by, r, u, (k2, d2) => {
      if (k2 === j || u.slots.alive[k2] === 0) return;
      const fall = 1 - Math.sqrt(d2) / r;
      u.poisonT[k2] = Math.max(u.poisonT[k2], 1.6);
      damageUnit(w, k2, base * (0.4 + 0.6 * fall), DamageType.Poison, bx, by, -1);
    });
    pushEvent(w.events, EV.Impact, bx, by, r, 0, u.team[j]);
  }

  // Whose death this was, in the ledger's terms: an enemy of the town pays
  // Souls, one of the town's own is a loss. A neutral is neither, which is what
  // `Alliance.None` is for.
  if (areHostile(u.team[j], Team.Player)) {
    stats.kills++;
    stats.killsByType[u.typeId[j]] = (stats.killsByType[u.typeId[j]] ?? 0) + 1;
    const souls =
      CORPSE_SOULS * (1 + t.threat * 0.06) * (w.faction.bonuses.resourceRate?.souls ?? 1) * w.mods.soulGain;
    w.res.souls += souls;
    statsOf(w).resourcesProduced.souls += souls;
    // The hero learns from the same kill the ledger records.
    awardHeroXp(w, HERO_XP_PER_KILL + t.threat * HERO_XP_PER_THREAT);
  } else if (alliedWith(u.team[j], Team.Player)) {
    awardCompanionXp(w, HERO_XP_PER_KILL + t.threat * HERO_XP_PER_THREAT, Team.Invader);
    if (mourned) stats.losses++;
    if ((u.flags[j] & UF.Villager) !== 0) {
      w.morale -= 0.012;
      // The age bands pay for the death proportionally — a siege kills the
      // town, not just the workforce. This is the one door every real
      // villager death comes through, which is what lets a sword, a shell, a
      // card and the winter dieback all charge the same ledger. Removals that
      // are not deaths (the warren's musters, the Hollow's toll) go through
      // `u.kill` and deliberately never reach it. See `systems/demography.ts`.
      onVillagerDeath(w);
    }
    /**
     * The reap. Three races' rules text has promised since the race contract
     * landed that "every body that falls on your ground yields them — almost
     * nobody else's do", and `reapsSouls` was read by nothing: the one economy
     * fact that distinguishes the Drowned Court, the Crimson Assize and the
     * Litany was a sentence on a selection screen. Enemy corpses already pay
     * everyone (the line above, which the whole soul economy is tuned on and
     * which deliberately does not change here) — what a reaper adds is the
     * *other* half of the field: their own fallen. Same formula as the enemy
     * line, so a body is worth what a body is worth, whoever it grew up as.
     */
    if (race(w.raceId).economy.reapsSouls) {
      const reaped =
        CORPSE_SOULS * (1 + t.threat * 0.06) * (w.faction.bonuses.resourceRate?.souls ?? 1) * w.mods.soulGain;
      w.res.souls += reaped;
      statsOf(w).resourcesProduced.souls += reaped;
    }
    // The Pale Writ and the Graveyard Shift: some of yours get back up.
    if (w.mods.corpseRise > 0 && (u.flags[j] & UF.Summoned) === 0 && chance(w.rng, w.mods.corpseRise)) {
      const risen = spawnUnit(w, unitTypeIndex('summon_skeleton'), Team.Player, u.x[j], u.y[j]);
      if (risen >= 0) {
        w.units.lifeT[risen] = 20;
        w.units.flags[risen] |= UF.Summoned;
      }
    }
  }

  // Nearby friends notice — unless there was nothing to notice.
  if (mourned) {
    const team = u.team[j];
    w.spatial.query(u.x[j], u.y[j], 4.2, u, (k, d2) => {
      // Your own side feels it. Alliance, not banner: men do notice the
      // company beside them going down, whoever pays it.
      if (!alliedWith(u.team[k], team) || k === j) return;
      const fall = 1 - Math.sqrt(d2) / 4.2;
      u.morale[k] -= 0.075 * fall * (big ? 2.2 : 1);
    });
  }

  const cid = u.companyId[j];
  if (cid >= 0) {
    const c = findCompany(w, cid);
    if (c) {
      c.losses++;
      c.lastLossTick = w.tick;
    }
  }
  if (attacker >= 0 && u.slots.alive[attacker] === 1) {
    const acid = u.companyId[attacker];
    if (acid >= 0) {
      const ac = findCompany(w, acid);
      if (ac) {
        ac.kills++;
        ac.xp += t.threat * (ac.team === Team.Player ? w.mods.veterancyRate : 1);
      }
    }
  }

  u.kill(j);
}

export function damageBuilding(w: World, b: number, raw: number, dmgType: number): void {
  if (!w.buildings.isAlive(b)) return;
  const resist = w.buildings.team[b] === Team.Player ? w.mods.siegeResist : 1;
  const dmg = raw * damageMultiplier(dmgType, ArmourType.Structure) * resist;
  w.buildings.hp[b] -= dmg;
  w.buildings.lastHitTick[b] = w.tick;
  const def = buildingType(w.buildings.typeId[b]);
  const cx = w.buildings.tx[b] + def.w / 2;
  const cy = w.buildings.ty[b] + def.h / 2;
  pushEvent(w.events, EV.StructureHit, cx, cy, dmg, w.buildings.typeId[b], w.buildings.team[b]);

  /**
   * The Coin of the Ferry: once per Siege the Hearth survives a killing blow at
   * 1 health.
   *
   * Checked before the death branch rather than inside it, because the Hearth
   * going out ends the run and there is no undoing it afterwards.
   */
  if (w.buildings.hp[b] <= 0 && b === w.hearth && alliedWith(w.buildings.team[b], Team.Player) && w.mods.hearthSave > 0 && !w.hearthSaved) {
    w.hearthSaved = true;
    w.buildings.hp[b] = 1;
    pushLog(w.events, 'The Hearth gutters, and does not go out. Somebody paid the ferryman.', LogTone.Good);
    return;
  }

  if (w.buildings.hp[b] <= 0) {
    pushEvent(w.events, EV.StructureDown, cx, cy, def.w * def.h, w.buildings.typeId[b], w.buildings.team[b]);
    if (b === w.hearth) {
      const enemyHearth = areHostile(w.buildings.team[b], Team.Player);
      if (w.battle) w.battle.outcome = enemyHearth ? 1 : -1;
      w.gameOver = !w.battle || !enemyHearth;
      w.causeOfDeath = 'The Hearth went out.';
      pushLog(w.events, enemyHearth ? 'The enemy Hearth falls. The city is taken.' : 'The Hearth goes out. The dark does not gloat; it simply arrives.', enemyHearth ? LogTone.Good : LogTone.Dire);
    } else {
      statsOf(w).buildingsLost++;
      pushLog(w.events, `${def.name} is lost.`, LogTone.Bad);
    }
    // Import cycle avoided: the caller-side removal happens in cleanup.
    w.buildings.hp[b] = 0;
  }
}

export function damageWall(w: World, tile: number, raw: number, dmgType: number): void {
  if (w.map.wall[tile] === WallKind.None) return;
  const dmg = raw * damageMultiplier(dmgType, ArmourType.Structure) * w.mods.siegeResist;
  w.map.wallHp[tile] -= dmg;
  // The approach is now cheaper through this section than it was. Let the siege
  // heartbeat know there is actually something to re-read.
  w.dirty.flowWeights = true;
  const x = (tile % MAP_W) + 0.5;
  const y = ((tile / MAP_W) | 0) + 0.5;
  pushEvent(w.events, EV.StructureHit, x, y, dmg, 1, w.wallTeam);

  if (w.map.wallHp[tile] <= 0) {
    const kind = w.map.wall[tile];
    const tier = w.map.wallTier[tile];
    const wasGate = kind === WallKind.Gate;
    const wasTower = kind === WallKind.Tower;
    w.map.wall[tile] = WallKind.None;
    w.map.wallHp[tile] = 0;
    w.map.wallMaxHp[tile] = 0;
    w.map.wallTier[tile] = 0;
    w.dirty.flow = true;
    w.map.wallChanged();
    statsOf(w).wallsLost++;

    /**
     * The section goes, and everybody on it goes with it.
     *
     * This is the moment the whole feature turns on. Up to that instant a man on
     * this stone could not be touched by anything on the ground; a second later
     * he is lying in the rubble, stunned, hurt, and standing in the one square
     * of the circuit where the rule does not hold. The men on the sections
     * *either side* keep their footing and lose their protection, because
     * `climbable` reads the hole beside them — so a breach spreads outward along
     * the wall rather than being one bad tile.
     */
    unmountTile(w, tile, fallen);
    for (const i of fallen) {
      const u = w.units;
      if (u.slots.alive[i] === 0) continue;
      u.stunT[i] = Math.max(u.stunT[i], FALL_STUN);
      u.morale[i] = Math.max(0, u.morale[i] - 0.3);
      u.hp[i] -= u.maxHp[i] * FALL_DAMAGE;
      u.lastHitTick[i] = w.tick;
      pushEvent(w.events, EV.Hit, u.x[i], u.y[i], u.maxHp[i] * FALL_DAMAGE, u.typeId[i], u.team[i]);
      if (u.hp[i] <= 0) killUnit(w, i, -1);
    }

    pushEvent(w.events, EV.Breach, x, y, wasGate || wasTower ? 2 : 1, tier, w.wallTeam);
    pushLog(
      w.events,
      wasGate
        ? 'The gate is down. They are inside.'
        : wasTower
          ? 'The tower comes down, and the men on it with it.'
          : fallen.length > 0
            ? `A breach — and ${fallen.length} of the watch went down with the wall.`
            : 'A breach — the wall is open.',
      LogTone.Dire,
    );
    w.morale -= wasGate ? 0.05 : wasTower ? 0.04 : 0.02;
  }
}

/** Scratch for `unmountTile`; the tick is single-threaded and this is consumed at once. */
const fallen: number[] = [];

// ── Morale & routing ───────────────────────────────────────────────────────

export function stepMorale(w: World, dt: number): void {
  const u = w.units;
  const high = u.slots.high;

  for (let i = 0; i < high; i++) {
    if (u.slots.alive[i] === 0) continue;

    // Fatigue: running costs, standing recovers. Recovery is deliberately much
    // slower than the drain, so pacing a battle is a real decision.
    const speed2 = u.vx[i] * u.vx[i] + u.vy[i] * u.vy[i];
    const t = unitType(u.typeId[i]);
    if (speed2 > 0.55) {
      u.fatigue[i] = Math.min(1, u.fatigue[i] + (0.055 * dt) / Math.max(0.2, t.stamina));
    } else {
      u.fatigue[i] = Math.max(0, u.fatigue[i] - 0.02 * dt);
    }
    // Exhaustion frays the nerves as much as the legs.
    if (u.fatigue[i] > 0.7) u.morale[i] -= (u.fatigue[i] - 0.7) * 0.12 * dt;

    // Status timers tick down here so every system sees a consistent value.
    if (u.slowT[i] > 0) u.slowT[i] -= dt;
    if (u.fearT[i] > 0) u.fearT[i] -= dt;
    if (u.stunT[i] > 0) u.stunT[i] -= dt;
    if (u.rallyT[i] > 0) u.rallyT[i] -= dt;
    if (u.shieldT[i] > 0) u.shieldT[i] -= dt;
    if (u.madnessT[i] > 0) u.madnessT[i] -= dt;
    if (u.healT[i] > 0) {
      u.healT[i] -= dt;
      u.hp[i] = Math.min(u.maxHp[i], u.hp[i] + u.maxHp[i] * 0.06 * dt);
    }
    if (u.poisonT[i] > 0) {
      u.poisonT[i] -= dt;
      damageUnit(w, i, 6 * dt, DamageType.Poison, u.x[i], u.y[i]);
      if (u.slots.alive[i] === 0) continue;
    }
    if (u.lifeT[i] > 0) {
      u.lifeT[i] -= dt;
      if (u.lifeT[i] <= 0) {
        // Its time was simply up. Not a casualty, and nobody mourns it.
        killUnit(w, i, -1, false);
        continue;
      }
    }

    const flags = u.flags[i];
    const fearless = (flags & (UF.Undead | UF.Summoned | UF.Champion | UF.Siege)) !== 0;

    if (u.state[i] === UnitState.Routing) {
      u.stateT[i] -= dt;
      // An invader that makes it off the field has left the battle. Without
      // this a wave never ends: three routed grunts sit against the map edge
      // and the siege waits for them forever.
      // Anybody at war with the town who reaches the edge has left the battle.
      if (areHostile(u.team[i], Team.Player) && offTheField(u.x[i], u.y[i])) {
        pushEvent(w.events, EV.Death, u.x[i], u.y[i], 0, u.typeId[i], u.team[i], 0, 0);
        u.kill(i);
        continue;
      }
      if (u.stateT[i] <= 0) {
        /**
         * Past the breaking point, a router whose clock runs out has *got
         * away*, wherever he happens to be standing. The withdraw mechanism
         * in `director.ts` promises a siege terminates because routers "run
         * for the edge" — but a router steers by straight radial line, and
         * the harness caught eleven of them pinned in inside corners (two
         * woodcutters, the town wall) for three hundred game-minutes, jogging
         * in place while their frozen `fearT` re-routed them every rally. He
         * found a ditch; the siege does not owe us his walking animation.
         * Sieges only, and only invaders: a battle's routers are resolve
         * arithmetic, and a defender who breaks is still your subject.
         */
        if (
          areHostile(u.team[i], Team.Player) &&
          !w.battle &&
          w.phase === Phase.Siege &&
          w.phaseT > SIEGE_BREAKING_POINT
        ) {
          pushEvent(w.events, EV.Death, u.x[i], u.y[i], 0, u.typeId[i], u.team[i], 0, 0);
          u.kill(i);
          continue;
        }
        u.state[i] = UnitState.Idle;
        u.morale[i] = 0.55;
      }
      continue;
    }

    // Drift back toward the settlement's baseline morale.
    // The settlement's own morale is the town's, so it steadies the town's
    // men — and a banner fighting alongside them, who is standing in the same
    // fed, paid, walled place. Everyone else keeps the flat besieger's nerve.
    const baseline = alliedWith(u.team[i], Team.Player) ? clamp01(0.45 + w.morale * 0.55) : 0.85;
    const rate = u.rallyT[i] > 0 ? 0.9 : 0.16;
    u.morale[i] += (baseline - u.morale[i]) * Math.min(1, rate * dt);
    if (u.fearT[i] > 0) u.morale[i] -= 0.4 * dt;
    u.morale[i] = clamp01(u.morale[i]);
    if (u.team[i] === Team.Player && w.mods.moraleFloor > 0) {
      u.morale[i] = Math.max(u.morale[i], w.mods.moraleFloor);
    }

    if (!fearless && u.morale[i] < ROUT_THRESHOLD) {
      u.state[i] = UnitState.Routing;
      u.stateT[i] = ROUT_SECONDS;
      u.targetH[i] = -1;
      u.targetBuilding[i] = -1;
      u.targetWall[i] = -1;
      // A man who has broken has left his post. `stepGarrison` will put him back
      // on the wall when he comes to his senses, if there is still a wall.
      if (u.wallAt[i] === -1) u.wallAt[i] = standingOn(w, i);
      u.wallTile[i] = -1;
      pushEvent(w.events, EV.Rout, u.x[i], u.y[i], 1, u.typeId[i], u.team[i]);
    }
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Within a tile and a half of the map edge — close enough to have got away. */
function offTheField(x: number, y: number): boolean {
  return x < 1.5 || y < 1.5 || x > MAP_W - 1.5 || y > MAP_H - 1.5;
}

function sq(v: number): number {
  return v * v;
}

/** Used by powers and abilities that want a random point in a disc. */
export function scatterPoint(w: World, x: number, y: number, r: number): { x: number; y: number } {
  const a = nextFloat(w.rng) * Math.PI * 2;
  const d = Math.sqrt(nextFloat(w.rng)) * r;
  return { x: x + Math.cos(a) * d, y: y + Math.sin(a) * d };
}
